<template>
  <div data-testid="credit-purchases-table">
    <data-table
      test-id-prefix="credit-purchases"
      :config="tableConfig"
      :data="enrichedRows"
      display-mode="table"
      :refreshButton="{ show: true }"
      @refresh="emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatCredits } from '../../assistant/formatCredits'
import { permissionsService } from '../../../services/permissionsService'
import { useCreditPacks } from '../../../composables/useCreditPacks'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import type { AiCredits_PurchaseRow } from '../../../contracts/AiCredits_PurchaseRow'

const { t, n } = useI18n()
const { packs } = useCreditPacks()

interface Props {
  rows: AiCredits_PurchaseRow[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  refresh: []
}>()

// Display-formatting locale per currency — mirrors CreditPackMarketsPanel.
const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

interface EnrichedPurchaseRow extends AiCredits_PurchaseRow {
  franchise_name: string
  pack_name: string
  amount_display: string
}

function packNameById(packId: number): string {
  const pack = packs.value.find((p) => p.id === packId)
  return pack ? pack.name : `#${packId}`
}

// ai_credit_purchases' own status vocabulary (Ai_credit_purchase_model::STATUS_*)
// — pending/confirmed/failed/expired. Distinct from subscription_transactions'
// vocabulary (StaffTransactionsTable) even where labels happen to overlap; do
// not merge the two maps, they cover different tables (billing-staff-console
// skill §"two incompatible status VOCABULARIES").
const statusBadgeMap = computed<Record<string, { label: string; severity: string }>>(() => ({
  pending:   { label: t('Pendente'), severity: 'warn' },
  confirmed: { label: t('Confirmada'), severity: 'success' },
  failed:    { label: t('Falhou'), severity: 'danger' },
  // A purchase abandoned mid-flow, closed by the sweep — distinct from 'failed'
  // (attempted and refused). Never terminal: a late webhook can still confirm
  // it (model docblock), so this is not a dead end for the franchise.
  expired:   { label: t('Abandonada'), severity: 'secondary' },
}))

function statusBadgeConfig(row: Record<string, unknown>): { label: string; severity: string; tooltip: string } {
  const status = row.status as string | undefined
  const cfg = status ? (statusBadgeMap.value[status] ?? { label: status, severity: 'secondary' }) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

function formatAmount(row: AiCredits_PurchaseRow): string {
  if (row.price_cents == null) return '—'
  const locale = CURRENCY_LOCALE[row.currency] ?? 'pt-BR'
  const factor = currencyFactor(locale, row.currency)
  return n(row.price_cents / factor, { style: 'currency', currency: row.currency, currencyDisplay: 'symbol' })
}

const enrichedRows = computed<EnrichedPurchaseRow[]>(() =>
  props.rows.map((row) => ({
    ...row,
    franchise_name: permissionsService.getFranchiseName(row.franquia_id) || `#${row.franquia_id}`,
    pack_name: packNameById(row.pack_id),
    amount_display: formatAmount(row),
  })),
)

const tableConfig = computed(() => ({
  tableId: 'creditPurchasesTable',
  title: t('Compras de Créditos'),
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
    },
    {
      data: 'franchise_name',
      title: t('Franquia'),
      sortable: true,
    },
    {
      data: 'pack_name',
      title: t('Pacote'),
      sortable: true,
    },
    {
      data: 'charge_id',
      title: t('Charge ID'),
      sortable: true,
    },
    {
      data: 'amount_display',
      title: t('Valor'),
      sortable: false,
    },
    {
      data: 'credits_granted',
      title: t('Créditos concedidos'),
      sortable: true,
      cellRenderer: ({ value }: { value: unknown }) =>
        value == null ? '—' : `${formatCredits(value as number)} cr`,
    },
    {
      data: 'status',
      title: t('Status'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: statusBadgeConfig,
    },
    {
      data: 'created_at',
      title: t('Data'),
      sortable: true,
      renderType: 'datetime',
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) =>
    row?.id != null ? `credit-purchases-row-${row.id as number}` : '',
}))
</script>
